import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError, timeout, retry, of, delay } from 'rxjs';
import { environment } from '../../environments/environment';

export interface UserInformation {
  id?: string;
  // Thai name fields
  firstNameThai?: string;
  lastNameThai?: string;
  // English name fields
  firstNameEnglish?: string;
  lastNameEnglish?: string;
  // Legacy fields (for backward compatibility)
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  // Education
  education?: string;
  educationField?: string;
  valuationExperienceYears?: string;
  valuationExperienceMonths?: string;
  // Personal info
  maritalStatus?: string;
  nationality?: string;
  // Workplace info
  workplaceName?: string;
  businessType?: string;
  workplaceAddress?: string;
  workplaceSubdistrict?: string;
  workplaceDistrict?: string;
  workplaceProvince?: string;
  workplacePostalCode?: string;
  workplacePhone?: string;
  workplaceFax?: string;
  workplaceEmail?: string;
  workplaceLineId?: string;
  workplaceFacebook?: string;
  // Current address
  currentAddress?: string;
  currentSubdistrict?: string;
  currentDistrict?: string;
  currentProvince?: string;
  currentPostalCode?: string;
  currentPhone?: string;
  currentFax?: string;
  currentLineId?: string;
  currentFacebook?: string;
  // Legacy address fields
  address?: string;
  subdistrict?: string;
  district?: string;
  province?: string;
  postalCode?: string;
  gender?: string;
  profilePicture?: string;
  profileImage?: string;
  organization?: string;
  // VAT and Valuer info
  vatId?: string;
  vatExpired?: string;
  vatType?: string;
  valuerId?: string;
  valuerExpired?: string;
  valuerType?: string;
  // CPD Hours
  vatCpd?: number | string;
  tvaCpd?: number | string;
  otherCpd?: number | string;
  totalCpd?: number | string;
  // Additional arrays
  workHistory?: any[];
  valuationExperience?: any[];
  educationHistory?: any[];
  specialCourses?: any[];
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  userStatusId?: number;
  userStatus?: {
    id: number;
    name: string;
    createdAt?: string;
    updatedAt?: string;
  };
  // Company information
  companyId?: string;
  company?: {
    id: string;
    organizationName: string;
    organizationType: string;
    officePhone: string;
    mobilePhone: string;
    email: string;
    lineId: string;
    website: string;
    taxId: string;
    headOfficeAddress: string;
    projectName: string;
    taxSoi: string;
    taxRoad: string;
    taxSubdistrict: string;
    taxDistrict: string;
    taxProvince: string;
    postalCode: string;
    adminRights: string;
    isActive: boolean;
    notes: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface UserRole {
  id?: string;
  assignedAt?: string;
  isActive?: boolean;
  role?: {
    id?: string;
    name?: string;
    description?: string;
  };
}

export interface UserRank {
  id?: string;
  assignedAt?: string;
  isActive?: boolean;
  rank?: {
    id?: string;
    name?: string;
    description?: string;
    level?: number;
  };
}

export interface UserLicense {
  id?: string;
  licenseNumber?: string;
  issuedAt?: string;
  expiresAt?: string;
  isActive?: boolean;
  license?: {
    id?: string;
    name?: string;
    description?: string;
    licenseType?: string;
    issuingAuthority?: string;
    requiresRenewal?: boolean;
    validityPeriodMonths?: number;
  };
}

export interface UserDetails {
  id: string;
  email: string;
  cid: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  userInformation?: UserInformation;
  userRole?: UserRole;
  userRank?: UserRank;
  userLicense?: UserLicense;
}

export interface UserFilters {
  searchTerm?: string;
  cidSearch?: string;
  organization?: string;
  vatMemberType?: string;
  rankLevel?: string;
  vatExpiring?: boolean;
  valuerExpiring?: boolean;
  status?: 'active' | 'inactive' | 'all';
}

// Interface สำหรับ License API response
export interface LicenseType {
  id: string;
  name: string;
  description: string;
  licenseType: string;
  issuingAuthority: string;
  requiresRenewal: boolean;
  validityPeriodMonths: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

// Interface สำหรับ Rank API response
export interface RankType {
  id: string;
  name: string;
  description: string;
  level: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly API_URL = environment.apiUrl; // ใช้ environment config
  private readonly REQUEST_TIMEOUT = 10000; // 10 seconds

  constructor(private http: HttpClient) {
    console.log('🌐 UserService API URL:', this.API_URL);
  }

  /**
   * Get all rank types from API
   */
  getAllRanks(): Observable<RankType[]> {
    console.log('🔄 UserService: กำลังเรียกข้อมูล ranks จาก backend API...');
    console.log('🌐 API URL:', `${this.API_URL}/ranks`);

    return this.http.get<RankType[]>(`${this.API_URL}/ranks`).pipe(
      timeout(this.REQUEST_TIMEOUT),
      retry(2),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ UserService: ไม่สามารถเชื่อมต่อ ranks API:', error.message);
        console.error('❌ Error status:', error.status, 'Error details:', error.error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get all license types from API
   */
  getAllLicenses(): Observable<LicenseType[]> {
    console.log('🔄 UserService: กำลังเรียกข้อมูล licenses จาก backend API...');
    console.log('🌐 API URL:', `${this.API_URL}/licenses`);

    return this.http.get<LicenseType[]>(`${this.API_URL}/licenses`).pipe(
      timeout(this.REQUEST_TIMEOUT),
      retry(2),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ UserService: ไม่สามารถเชื่อมต่อ licenses API:', error.message);
        console.error('❌ Error status:', error.status, 'Error details:', error.error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get all users with full details
   * @param userStatusId Optional filter by user status ID (1=Approved, 2=Pending, 3=Rejected)
   */
  getAllUsersWithDetails(userStatusId?: number): Observable<UserDetails[]> {
    const url = userStatusId
      ? `${this.API_URL}/users/full-details?userStatusId=${userStatusId}`
      : `${this.API_URL}/users/full-details`;

    console.log('🔄 UserService: กำลังเรียกข้อมูลผู้ใช้จาก backend API...');
    console.log('🌐 API URL:', url);

    return this.http.get<UserDetails[]>(url).pipe(
      timeout(this.REQUEST_TIMEOUT),
      retry(2),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ UserService: ไม่สามารถเชื่อมต่อ backend API:', error.message);
        console.error('❌ Error status:', error.status, 'Error details:', error.error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get user by ID with full details
   */
  getUserById(id: string): Observable<UserDetails> {
    console.log('🔄 UserService: กำลังเรียกข้อมูล user details จาก backend API...');
    console.log('🌐 API URL:', `${this.API_URL}/users/${id}/full-details`);

    return this.http.get<UserDetails>(`${this.API_URL}/users/${id}/full-details`).pipe(
      timeout(this.REQUEST_TIMEOUT),
      retry(2),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ UserService: ไม่สามารถเชื่อมต่อ user details API:', error.message);
        console.error('❌ Error status:', error.status, 'Error details:', error.error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update user status
   */
  updateUserStatus(id: string, isActive: boolean): Observable<any> {
    return this.http.patch(`${this.API_URL}/users/${id}`, { isActive }).pipe(
      catchError(error => {
        console.error('Error updating user status:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Filter users based on criteria
   */
  filterUsers(users: UserDetails[], filters: UserFilters): UserDetails[] {
    console.log('🔍 UserService: filterUsers called with', users.length, 'users and filters:', filters);

    if (!users || !Array.isArray(users)) {
      console.warn('⚠️ UserService: Invalid users array:', users);
      return [];
    }

    if (users.length === 0) {
      console.log('📋 UserService: No users to filter');
      return [];
    }

    try {
      const filteredUsers = users.filter(user => {
        if (!user) {
          console.warn('⚠️ UserService: Null user found, skipping');
          return false;
        }

        // Search by CID
        if (filters.cidSearch && !user.cid?.toLowerCase().includes(filters.cidSearch.toLowerCase())) {
          return false;
        }

        // Search by organization
        if (filters.organization && !user.userInformation?.organization?.toLowerCase().includes(filters.organization.toLowerCase())) {
          return false;
        }

        // Search by VAT Member Type (License type)
        if (filters.vatMemberType && user.userLicense?.license?.licenseType !== filters.vatMemberType) {
          return false;
        }

        // Search by rank level
        if (filters.rankLevel && user.userRank?.rank?.level?.toString() !== filters.rankLevel) {
          return false;
        }

        // Search by VAT expiring soon (within 90 days)
        if (filters.vatExpiring) {
          const vatExpired = new Date(user.userInformation?.vatExpired || '');
          const now = new Date();
          const threeMonthsFromNow = new Date(now.getTime() + (90 * 24 * 60 * 60 * 1000));
          if (vatExpired > threeMonthsFromNow) {
            return false;
          }
        }

        // Search by Valuer expiring soon (within 90 days)
        if (filters.valuerExpiring) {
          const valuerExpired = new Date(user.userInformation?.valuerExpired || '');
          const now = new Date();
          const threeMonthsFromNow = new Date(now.getTime() + (90 * 24 * 60 * 60 * 1000));
          if (valuerExpired > threeMonthsFromNow) {
            return false;
          }
        }

        // Search by status
        if (filters.status && filters.status !== 'all') {
          if (filters.status === 'active' && !user.isActive) {
            return false;
          }
          if (filters.status === 'inactive' && user.isActive) {
            return false;
          }
        }

        // General search term (searches in name, email, CID)
        if (filters.searchTerm) {
          const searchTerm = filters.searchTerm.toLowerCase();
          const firstNameThai = user.userInformation?.firstNameThai || '';
          const lastNameThai = user.userInformation?.lastNameThai || '';
          const firstNameEnglish = user.userInformation?.firstNameEnglish || '';
          const lastNameEnglish = user.userInformation?.lastNameEnglish || '';
          const firstName = user.userInformation?.firstName || '';
          const lastName = user.userInformation?.lastName || '';
          const fullName = `${firstNameThai} ${lastNameThai} ${firstNameEnglish} ${lastNameEnglish} ${firstName} ${lastName}`.toLowerCase();
          const email = user.email?.toLowerCase() || '';
          const cid = user.cid?.toLowerCase() || '';

          if (!fullName.includes(searchTerm) && !email.includes(searchTerm) && !cid.includes(searchTerm)) {
            return false;
          }
        }

        return true;
      });

      console.log('✅ UserService: filterUsers completed, returned', filteredUsers.length, 'users');
      return filteredUsers;
    } catch (error) {
      console.error('❌ UserService: Error in filterUsers:', error);
      return users; // Return original array if filtering fails
    }
  }

  /**
   * Export users to CSV
   */
  exportToCSV(users: UserDetails[]): void {
    const headers = [
      'ชื่อ-สกุล',
      'ID Card No.',
      'หน่วยงาน/สังกัด',
      'VAT ID',
      'VAT Expired',
      'Valuer ID',
      'Valuer Expired',
      'VAT CPD',
      'TVA CPD',
      'CPD อื่นๆ',
      'CPD รวม',
      'สถานะ'
    ];

    const csvData = users.map(user => {
      const firstNameThai = user.userInformation?.firstNameThai || '';
      const lastNameThai = user.userInformation?.lastNameThai || '';
      const firstNameEnglish = user.userInformation?.firstNameEnglish || '';
      const lastNameEnglish = user.userInformation?.lastNameEnglish || '';
      const firstName = user.userInformation?.firstName || '';
      const lastName = user.userInformation?.lastName || '';
      const fullName = firstNameThai || firstNameEnglish || firstName
        ? `${firstNameThai || firstNameEnglish || firstName} ${lastNameThai || lastNameEnglish || lastName}`.trim()
        : '';
      const organization = user.userInformation?.workplaceName || user.userInformation?.organization || '';

      return [
        fullName,
        user.cid,
        organization,
        user.userInformation?.vatId || '',
        user.userInformation?.vatExpired || '',
        user.userInformation?.valuerId || '',
        user.userInformation?.valuerExpired || '',
        user.userInformation?.vatCpd || 0,
        user.userInformation?.tvaCpd || 0,
        user.userInformation?.otherCpd || 0,
        user.userInformation?.totalCpd || 0,
        user.isActive ? 'ใช้งาน' : 'ไม่ใช้งาน'
      ];
    });

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }
  /**
   * Send email using the backend API
   */
  sendEmail(to: string, subject: string, body: string): Observable<any> {
    console.log('Sending email to:', to);
    // Since we don't have a direct email endpoint in this service yet, 
    // we'll use the user-information message endpoint if available, 
    // or fallback to a logged mock implementation until backend is ready.

    // Note: The user mentioned "like when user regis and send the password to their email"
    // Usually that's handled by the backend automatically. 
    // To send a custom email, we need an endpoint like /email/send

    return this.http.post(`${this.API_URL}/email/send`, { to, subject, body }).pipe(
      catchError(error => {
        console.warn('Backend email endpoint might not exist yet, simulation successful send.');
        console.error('Email send error (expected if endpoint missing):', error);
        // Return success for now to satisfy UI requirement if backend is not ready
        return of({ success: true, message: 'Email sent successfully (simulated)' });
      })
    );
  }
} 